/**
 * roles.js — Frontend mirror of backend rbac.py canonical role model.
 * ==================================================================
 *
 * Single source of truth for role names + legacy alias normalisation.
 * Mirrors backend/rbac.py::TEAM_ROLE_MAPPING so the frontend never
 * disagrees with the backend permission checks.
 *
 * Canonical team roles (from rbac.py):
 *   team_admin, coach, team_manager, player, parent
 *
 * Legacy aliases (still present in older user docs / memberships):
 *   head_coach       → team_admin
 *   assistant_coach  → coach
 *   admin            → team_admin  (alias on a team membership only)
 *
 * Canonical org roles:
 *   org_admin, org_finance, org_staff
 *
 * Canonical global roles:
 *   app_admin, app_support, user
 */

// ─────────────────────────────────────────────────────────────
//  Canonical constants
// ─────────────────────────────────────────────────────────────
export const GLOBAL_ROLES = Object.freeze({
  APP_ADMIN: 'app_admin',
  APP_SUPPORT: 'app_support',
  USER: 'user',
});

export const ORG_ROLES = Object.freeze({
  ORG_ADMIN: 'org_admin',
  ORG_FINANCE: 'org_finance',
  ORG_STAFF: 'org_staff',
});

export const TEAM_ROLES = Object.freeze({
  TEAM_ADMIN: 'team_admin',
  COACH: 'coach',
  TEAM_MANAGER: 'team_manager',
  PLAYER: 'player',
  PARENT: 'parent',
});

// Legacy → normalized team role. Matches backend TEAM_ROLE_MAPPING.
const TEAM_ROLE_ALIAS = Object.freeze({
  head_coach: TEAM_ROLES.TEAM_ADMIN,
  assistant_coach: TEAM_ROLES.COACH,
  coach: TEAM_ROLES.COACH,
  admin: TEAM_ROLES.TEAM_ADMIN,
  team_admin: TEAM_ROLES.TEAM_ADMIN,
  team_manager: TEAM_ROLES.TEAM_MANAGER,
  player: TEAM_ROLES.PLAYER,
  parent: TEAM_ROLES.PARENT,
});

/** Normalise any raw team role string to a canonical one. */
export function normalizeTeamRole(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const role = raw.toLowerCase();
  return Object.prototype.hasOwnProperty.call(TEAM_ROLE_ALIAS, role) ? TEAM_ROLE_ALIAS[role] : role;
}

/** Raw role from the requested team's canonical /me membership projection. */
export function getTeamRole(user, teamId) {
  const tid = teamId || user?.active_team_id || user?.team_id;
  const roles = user?.membership_roles;
  if (!tid || !roles || !Object.prototype.hasOwnProperty.call(roles, tid)) return null;
  const raw = roles[tid];
  if (typeof raw !== 'string') return null;
  const role = raw.toLowerCase();
  return Object.prototype.hasOwnProperty.call(TEAM_ROLE_ALIAS, role) ? role : null;
}

/** Missing membership grants no team role; legacy user fields are display data. */
export function getEffectiveTeamRole(user, teamId) {
  return normalizeTeamRole(getTeamRole(user, teamId));
}

/** Organization rights apply only to a verified team in that organization. */
export function isOrgAdminForTeam(user, teamId, team) {
  if (!user || !teamId || team?.id !== teamId || !team.organization_id) return false;
  const role = user.org_roles?.[team.organization_id];
  return ['org_admin', 'club_admin', 'owner'].includes(role);
}

// ─────────────────────────────────────────────────────────────
//  Permission predicates
// ─────────────────────────────────────────────────────────────

/** True for platform admins (highest level). */
export function isAppAdmin(user) {
  return user?.global_role === GLOBAL_ROLES.APP_ADMIN;
}

/** True for org-level admins on ANY of their orgs. */
export function isOrgAdmin(user) {
  if (!user) return false;
  const orgRoles = user.org_roles || {};
  return Object.values(orgRoles).includes(ORG_ROLES.ORG_ADMIN);
}

/**
 * Can the user manage team onboarding (invite codes, CSV import,
 * roster edits) for THIS specific team?
 *
 * Scoping (P0.4):
 *   • app_admin        — all teams.
 *   • org_admin        — ONLY teams inside their own organization
 *                        (matched via team.organization_id). Being an
 *                        org_admin somewhere else grants no rights here.
 *   • team_admin /
 *     coach /
 *     team_manager     — only the specific team where their canonical
 *                        (or legacy-aliased) membership role gives them
 *                        that role.
 *
 * Blocked:
 *   • player, parent
 *   • org_finance, org_staff (finance-only, no roster writes)
 *
 * @param {object|null} user   Auth-context user object.
 * @param {string|null} teamId Team id being accessed.
 * @param {object}      opts
 * @param {object|null} opts.team   Team doc (or minimal shape) with
 *                                   `organization_id`. Required to make
 *                                   an accurate org_admin decision. If
 *                                   missing we conservatively DENY
 *                                   org_admin unless the caller has
 *                                   established the team belongs to the
 *                                   user's org through another channel.
 */
export function canManageTeamOnboarding(user, teamId, opts = {}) {
  if (!user || !teamId) return false;
  if (isAppAdmin(user)) return true;

  // Team-scoped canonical role check — legacy aliases are normalised.
  const role = getEffectiveTeamRole(user, teamId);
  if (
    role === TEAM_ROLES.TEAM_ADMIN ||
    role === TEAM_ROLES.COACH ||
    role === TEAM_ROLES.TEAM_MANAGER
  ) {
    return true;
  }

  // Org-scoped admin: must be admin FOR THIS TEAM'S organization,
  // not just any org somewhere.
  return isOrgAdminForTeam(user, teamId, opts.team);
}

// ─────────────────────────────────────────────────────────────
//  Swedish display labels (matches backend rbac.py TEAM_ROLE_DISPLAY).
// ─────────────────────────────────────────────────────────────
const TEAM_ROLE_LABEL_SV = Object.freeze({
  team_admin: 'Lagadmin',
  coach: 'Tränare',
  team_manager: 'Lagledare',
  player: 'Spelare',
  parent: 'Förälder',
  // Legacy aliases still surfaced from older docs — display same as the
  // canonical mapping so UI never shows both "Head Coach" and "Lagadmin".
  head_coach: 'Huvudtränare',
  assistant_coach: 'Tränare',
  admin: 'Lagadmin',
});

/** Human-friendly Swedish label for a raw team role code. */
export function teamRoleLabelSv(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return TEAM_ROLE_LABEL_SV[raw.toLowerCase()] || raw;
}
